// @givmo/social — the composer: a new post, or a draft re-opened (SMM-WEB1; §SMM-8 point 2,
// "Composer" and "The Email Campaign entry").
//
// One text, an optional image and link, the accounts it goes to, and each account's post as that
// platform will take it — its text fitted to the platform's room until a person edits it for that
// account, its count against that room, and the platform's honest hints (Instagram needs an image
// and shows a link as plain words; Facebook shows a link as a preview; elsewhere the link is added at
// the end). Then Save draft, Schedule (a date and a time in the person's own zone, sent with its
// offset) or Publish now. Nothing is sent until one of the last two.
//
// The Email Campaign entry: an OPTIONAL campaign context, by deep link (`/social/new?context_kind=
// …&context_ref=…&context_label=…&link=…&at=…`) or by embed (`entry`). It tags the post (the
// context pair), prefills the link and a suggested time (the person confirms each), and offers the
// organization's Collateral images IN USE, each copied server-side into Social's own bucket when
// chosen (`…/collateral/{item}/social-image`: the library's bucket answers no browser a read).

import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Check, ImagePlus, Link2, RefreshCw, X } from 'lucide-react';
import { sdk, type SocialImageOut, type SocialPostOut } from '@givmo/api-client';
import {
  Button,
  Card,
  Dialog,
  ErrorMessage,
  Field,
  Icon,
  Textarea,
  cn,
  testIdProp,
  toast,
} from '@givmo/design-system';
import type { CollateralItem } from '@givmo/org-console';
import {
  EMPTY_COMPOSER,
  blockReason,
  channelsOf,
  composerBlocks,
  stateFromPost,
  variantViews,
  writeBody,
  type BlockReason,
  type Channel,
  type ComposerContext,
  type ComposerState,
  type VariantView,
} from './composer-model';
import { PlatformMark } from './platform-mark';
import { actionErrorWords } from './post-parts';
import { DeleteDraftDialog } from './posts-page';
import { canManageConnections, type SocialConfig } from './social-config';
import { startConnect } from './social-connect';
import {
  IMAGE_MAX_UPLOAD_BYTES,
  IMAGE_UPLOAD_MIME_TYPES,
  MAX_BASE_TEXT_LENGTH,
  charCount,
  grantFamilyOf,
  isoWithOffset,
  linkProblem,
  platformLabel,
  scheduleProblem,
} from './social-platforms';
import { useCollateralImages, useConnections, useRefreshSocial } from './social-queries';
import { forgetRescheduleTime } from './social-reschedule';
import { SOCIAL_PAGE_TEST_ID, SOCIAL_ROUTES, socialPath } from './social-routes';
import { clearStashedComposer, readStashedComposer, stashComposer } from './social-stash';
import { SocialPageShell, timeZoneName, whenWords } from './social-ui';
import { socialWords as w } from './social-words';

/** What the Email Campaign entry hands the composer, by deep link or embed. */
export interface ComposerEntry {
  context: ComposerContext | null;
  link: string | null;
  /** A suggested time beside the email series: a prefill the person confirms, never a send. */
  suggestedAt: string | null;
}

export const NO_ENTRY: ComposerEntry = { context: null, link: null, suggestedAt: null };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The deep link's entry, read tolerantly: anything malformed is simply not offered. */
export function readComposerEntry(search: string): ComposerEntry {
  const params = new URLSearchParams(search);
  const kind = params.get('context_kind');
  const ref = params.get('context_ref');
  const label = params.get('context_label')?.trim() ?? '';
  const link = params.get('link')?.trim() ?? '';
  const at = params.get('at') ?? '';
  const when = at ? new Date(at) : null;
  return {
    context:
      kind === 'fundraiser_campaign' && ref && UUID.test(ref)
        ? { kind, ref, label: label.length > 0 ? label.slice(0, 120) : null }
        : null,
    link: link.length > 0 && linkProblem(link) === null ? link : null,
    suggestedAt: when && !Number.isNaN(when.getTime()) ? when.toISOString() : null,
  };
}

function initialState(entry: ComposerEntry): ComposerState {
  return { ...EMPTY_COMPOSER, link: entry.link ?? '', context: entry.context };
}

/** The first hour a schedule starts on, and the last, in the person's own day: a post scheduled
 *  from the early morning starts at 9:00, and one from the evening the next morning at 9:00. */
const DAY_STARTS = 9;
const DAY_ENDS = 20;

/** The schedule dialog's starting time: the suggestion (or the time a post had before it was
 *  opened for editing) while it is still inside the window; else the first whole hour at least an
 *  hour away, inside the working day (4:39 AM starts at 9:00 AM, 10:20 AM at 12:00 PM, 7:40 PM at
 *  tomorrow's 9:00 AM). */
export function startingTime(suggestedAt: string | null, now: Date): Date {
  if (suggestedAt) {
    const suggested = new Date(suggestedAt);
    if (scheduleProblem(suggested, now) === null) return suggested;
  }
  const next = new Date(now.getTime() + 60 * 60 * 1000);
  if (next.getMinutes() !== 0 || next.getSeconds() !== 0 || next.getMilliseconds() !== 0) {
    next.setHours(next.getHours() + 1, 0, 0, 0);
  }
  if (next.getHours() < DAY_STARTS) {
    next.setHours(DAY_STARTS, 0, 0, 0);
  } else if (next.getHours() >= DAY_ENDS) {
    next.setDate(next.getDate() + 1);
    next.setHours(DAY_STARTS, 0, 0, 0);
  }
  return next;
}

/** The reason the post can't go yet, in the words beside the buttons. */
export function blockWords(reason: BlockReason | null): string | null {
  if (reason === null) return null;
  switch (reason.code) {
    case 'no_text':
      return w.blockNoText;
    case 'base_too_long':
      return w.baseTooLong;
    case 'bad_link':
      return w.blockBadLink;
    case 'no_channels':
      return w.blockNoChannels;
    case 'too_many_channels':
      return w.blockTooManyChannels;
    case 'variant': {
      const platform = platformLabel(reason.platform);
      switch (reason.problem) {
        case 'image_required':
          return w.blockImage(platform);
        case 'image_shape':
          return w.blockImageShape(platform);
        case 'too_many_hashtags':
          return `${w.problemHashtags.replace(/\.$/, '')}, or untick ${platform}.`;
        case 'too_many_mentions':
          return `${w.problemMentions.replace(/\.$/, '')}, or untick ${platform}.`;
        case 'text_required':
          return `Write ${platform}’s post, or untick ${platform}.`;
        default:
          return w.blockText(platform);
      }
    }
  }
}

const pad = (value: number) => String(value).padStart(2, '0');
const dateInput = (moment: Date) =>
  `${moment.getFullYear()}-${pad(moment.getMonth() + 1)}-${pad(moment.getDate())}`;
const timeInput = (moment: Date) => `${pad(moment.getHours())}:${pad(moment.getMinutes())}`;

export function ComposerPage({
  config,
  post = null,
  entry = NO_ENTRY,
}: {
  config: SocialConfig;
  /** A draft to re-open; null for a new post. */
  post?: SocialPostOut | null;
  entry?: ComposerEntry;
}) {
  const { orgRef } = config;
  const navigate = useNavigate();
  const refresh = useRefreshSocial(orgRef);
  const connections = useConnections(orgRef);
  const allChannels = useMemo(() => channelsOf(connections.data ?? []), [connections.data]);
  const channels = useMemo(
    () => allChannels.filter((channel) => !channel.needsReconnect),
    [allChannels],
  );
  const [state, setState] = useState<ComposerState>(() =>
    post
      ? stateFromPost(post, entry.context?.label ?? null)
      : (readStashedComposer(orgRef) ?? initialState(entry)),
  );
  // A post kept while its writer reconnected an account is the composer's now: forget the copy.
  useEffect(() => {
    if (post === null) clearStashedComposer(orgRef);
  }, [orgRef, post]);
  const [postId, setPostId] = useState<string | null>(post?.post_id ?? null);
  const [editing, setEditing] = useState<ReadonlySet<string>>(new Set());
  const [scheduling, setScheduling] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [unticking, setUnticking] = useState<Channel | null>(null);
  const [reconnecting, setReconnecting] = useState<string | null>(null);
  const [reconnectError, setReconnectError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const views = variantViews(state, channels);
  const blocks = composerBlocks(state, views);
  const reason = blockWords(blockReason(blocks, views));
  const lapsedChannels = allChannels.filter((channel) => channel.needsReconnect);
  const collateral = useCollateralImages(orgRef, state.context !== null);
  const set = (change: Partial<ComposerState>) =>
    setState((current) => ({ ...current, ...change }));

  const save = async (): Promise<SocialPostOut> => {
    const body = writeBody(state);
    if (postId === null) {
      const { data } = await sdk.orgSocialCreateOrgSocialPost({
        path: { org_ref: orgRef ?? '' },
        body,
        throwOnError: true,
      });
      setPostId(data.post_id);
      return data;
    }
    const { data } = await sdk.orgSocialUpdateOrgSocialPost({
      path: { org_ref: orgRef ?? '', post_id: postId },
      body,
      throwOnError: true,
    });
    return data;
  };

  const saveDraft = useMutation({
    mutationFn: save,
    onSuccess: async (saved) => {
      toast.success(w.draftSaved);
      await refresh();
      if (post === null) {
        void navigate({ to: socialPath({ page: 'post', postId: saved.post_id }) });
      }
    },
  });

  const publish = useMutation({
    mutationFn: async (when: Date | null) => {
      const saved = await save();
      const { data } = await sdk.orgSocialPublishOrgSocialPost({
        path: { org_ref: orgRef ?? '', post_id: saved.post_id },
        body: { scheduled_at: when ? isoWithOffset(when) : null },
        throwOnError: true,
      });
      return { data, when };
    },
    onSuccess: async ({ data, when }) => {
      setScheduling(false);
      forgetRescheduleTime(data.post_id);
      toast.success(when ? w.scheduled(whenWords(when.toISOString())) : w.published);
      await refresh();
      void navigate({ to: SOCIAL_ROUTES.posts });
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const { data } = await sdk.orgSocialUploadOrgSocialImage({
        path: { org_ref: orgRef ?? '' },
        body: { file },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (image: SocialImageOut) => {
      setImageError(null);
      set({ image });
    },
    onError: (error) => setImageError(actionErrorWords(error)),
  });

  const graphic = useMutation({
    mutationFn: async (item: CollateralItem) => {
      const { data } = await sdk.orgBrandUseOrgCollateralItemAsSocialImage({
        path: { org_ref: orgRef ?? '', item_id: item.id },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: (image: SocialImageOut) => {
      setImageError(null);
      set({ image });
    },
    onError: (error) => setImageError(actionErrorWords(error)),
  });

  const pickFile = (file: File | undefined) => {
    if (!file) return;
    if (!(IMAGE_UPLOAD_MIME_TYPES as readonly string[]).includes(file.type)) {
      setImageError(w.imageWrongType);
      return;
    }
    if (file.size > IMAGE_MAX_UPLOAD_BYTES) {
      setImageError(w.imageTooBig);
      return;
    }
    setImageError(null);
    upload.mutate(file);
  };

  const toggleChannel = (id: string) => {
    const chosen = state.channels.includes(id);
    if (chosen && state.overrides[id] !== undefined) {
      setUnticking(channels.find((channel) => channel.connectionId === id) ?? null);
      return;
    }
    flipChannel(id);
  };

  const flipChannel = (id: string) =>
    setState((current) => {
      const chosen = current.channels.includes(id);
      const overrides = { ...current.overrides };
      if (chosen) delete overrides[id];
      return {
        ...current,
        channels: chosen
          ? current.channels.filter((entry) => entry !== id)
          : [...current.channels, id],
        overrides,
      };
    });

  const act = (run: () => void) => {
    if (reason !== null) return;
    run();
  };

  // Reconnect a lapsed account without losing the post: saved as a draft first where it can be
  // (the person comes back to it), else kept in this tab for the composer to pick up (U7).
  const reconnect = async (channel: Channel) => {
    if (orgRef === null) return;
    setReconnecting(channel.connectionId);
    setReconnectError(null);
    let returnTo: string | null = null;
    const hasContent = state.text.trim().length > 0 || state.channels.length > 0;
    if (hasContent && reason === null) {
      try {
        const saved = await save();
        returnTo = socialPath({ page: 'post', postId: saved.post_id });
      } catch (error) {
        setReconnecting(null);
        setReconnectError(actionErrorWords(error));
        return;
      }
    } else if (hasContent && post === null) {
      stashComposer(orgRef, state);
      returnTo = SOCIAL_ROUTES.compose;
    }
    const refusal = await startConnect(
      orgRef,
      {
        kind: 'reconnect',
        connectionId: channel.connectionId,
        provider: channel.platform,
        family: grantFamilyOf(channel.platform),
      },
      undefined,
      returnTo,
    );
    if (refusal) {
      setReconnecting(null);
      setReconnectError(refusal);
    }
  };

  const busy = saveDraft.isPending || publish.isPending;
  const failure = saveDraft.error ?? publish.error;
  const linkError = linkProblem(state.link);
  const imageBusy = upload.isPending || graphic.isPending;

  return (
    <SocialPageShell
      title={post ? w.editDraftTitle : w.composeTitle}
      testId={SOCIAL_PAGE_TEST_ID}
      back={{ to: SOCIAL_ROUTES.posts, label: w.backToPosts }}
    >
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,400px)] items-start gap-24 max-lg:grid-cols-1">
        <Card className="flex flex-col gap-20 px-24 py-24" testId="social-composer">
          {state.context ? (
            <div
              className="flex items-center justify-between gap-12 border-b border-border-subtle pb-16"
              {...testIdProp('social-context')}
            >
              <span className="font-sans text-s14 font-w500 tracking-givmo text-text-strong">
                {w.campaignTagged(state.context.label ?? w.campaignUnnamed)}
              </span>
              <Button
                appearance="saas"
                tone="link"
                size="sm"
                onClick={() => set({ context: null })}
                testId="social-context-remove"
              >
                {w.removeCampaign}
              </Button>
            </div>
          ) : null}
          <section className="flex flex-col gap-8" aria-labelledby="social-post-to">
            <h2
              id="social-post-to"
              className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
            >
              {w.postTo}
            </h2>
            {connections.isPending ? null : channels.length === 0 && lapsedChannels.length === 0 ? (
              <NoChannels manager={canManageConnections(config)} />
            ) : (
              <div className="flex flex-wrap gap-8" {...testIdProp('social-channels')}>
                {channels.map((channel) => (
                  <ChannelChip
                    key={channel.connectionId}
                    channel={channel}
                    chosen={state.channels.includes(channel.connectionId)}
                    onToggle={() => toggleChannel(channel.connectionId)}
                  />
                ))}
              </div>
            )}
            {lapsedChannels.length > 0 ? (
              <ul className="flex flex-col gap-4" {...testIdProp('social-channels-lapsed')}>
                {lapsedChannels.map((channel) => (
                  <li
                    key={channel.connectionId}
                    className="flex flex-wrap items-center gap-x-8 gap-y-2 font-sans text-s13 tracking-givmo text-text-secondary"
                    {...testIdProp(`social-lapsed-${channel.connectionId}`)}
                  >
                    <PlatformMark platform={channel.platform} size={16} />
                    <span>{w.needReconnecting(channel.name)}</span>
                    {canManageConnections(config) ? (
                      <Button
                        appearance="saas"
                        tone="link"
                        size="sm"
                        leadingIcon={<Icon icon={RefreshCw} size={14} aria-hidden />}
                        loading={reconnecting === channel.connectionId}
                        disabled={reconnecting !== null || busy}
                        onClick={() => void reconnect(channel)}
                        testId={`social-lapsed-${channel.connectionId}-reconnect`}
                      >
                        {w.reconnect}
                      </Button>
                    ) : (
                      <span>{w.askAdminToReconnect}</span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
            {reconnectError ? (
              <ErrorMessage testId="social-reconnect-error">{reconnectError}</ErrorMessage>
            ) : null}
          </section>

          <div className="flex flex-col gap-6">
            <Textarea
              label={w.textLabel}
              value={state.text}
              onChange={(event) => set({ text: event.target.value })}
              placeholder={w.textPlaceholder}
              rows={7}
              error={blocks.includes('text_too_long')}
              errorMessage={blocks.includes('text_too_long') ? w.baseTooLong : undefined}
              testId="social-text"
            />
            <span
              className={cn(
                'self-end font-sans text-s12 tabular-nums tracking-givmo',
                charCount(state.text) > MAX_BASE_TEXT_LENGTH
                  ? 'text-danger-700'
                  : 'text-text-secondary',
              )}
              {...testIdProp('social-text-count')}
            >
              {charCount(state.text).toLocaleString()} / {MAX_BASE_TEXT_LENGTH.toLocaleString()}
            </span>
          </div>

          <section className="flex flex-col gap-8" aria-labelledby="social-image-label">
            <h2
              id="social-image-label"
              className="font-sans text-s13 font-w500 tracking-givmo text-text-secondary"
            >
              {w.imageLabel} <span className="font-w400">({w.optional})</span>
            </h2>
            <input
              ref={fileInput}
              type="file"
              accept={IMAGE_UPLOAD_MIME_TYPES.join(',')}
              className="hidden"
              onChange={(event) => {
                pickFile(event.target.files?.[0]);
                event.target.value = '';
              }}
              {...testIdProp('social-image-input')}
            />
            {state.image ? (
              <div className="flex items-center gap-16" {...testIdProp('social-image-attached')}>
                <img
                  src={state.image.url}
                  alt=""
                  className="size-[96px] shrink-0 rounded-md border border-border-subtle object-cover"
                  {...testIdProp('social-image-preview')}
                />
                <div className="flex flex-col items-start gap-4">
                  <span className="font-sans text-s12 tabular-nums tracking-givmo text-text-secondary">
                    {state.image.width && state.image.height
                      ? `${state.image.width} × ${state.image.height}`
                      : null}
                  </span>
                  <div className="flex items-center gap-4">
                    <Button
                      appearance="saas"
                      tone="secondary"
                      size="sm"
                      leadingIcon={<Icon icon={ImagePlus} size={14} aria-hidden />}
                      loading={upload.isPending}
                      disabled={imageBusy}
                      onClick={() => fileInput.current?.click()}
                      testId="social-image-replace"
                    >
                      {w.replaceImage}
                    </Button>
                    <Button
                      appearance="saas"
                      tone="ghost"
                      size="sm"
                      leadingIcon={<Icon icon={X} size={14} aria-hidden />}
                      disabled={imageBusy}
                      onClick={() => set({ image: null })}
                      testId="social-image-remove"
                    >
                      {w.removeImage}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-12">
                <Button
                  appearance="saas"
                  tone="secondary"
                  size="sm"
                  leadingIcon={<Icon icon={ImagePlus} size={16} aria-hidden />}
                  loading={upload.isPending}
                  disabled={imageBusy}
                  onClick={() => fileInput.current?.click()}
                  testId="social-image-add"
                >
                  {w.addImage}
                </Button>
                <span className="font-sans text-s12 tracking-givmo text-text-secondary">
                  {w.imageHint}
                </span>
              </div>
            )}
            {state.context !== null && !state.image && (collateral.data ?? []).length > 0 ? (
              <CollateralStrip
                items={collateral.data ?? []}
                busyId={graphic.isPending ? (graphic.variables?.id ?? null) : null}
                disabled={imageBusy}
                onUse={(item) => graphic.mutate(item)}
              />
            ) : null}
            {imageError ? (
              <ErrorMessage testId="social-image-error">{imageError}</ErrorMessage>
            ) : null}
          </section>

          <Field
            label={`${w.linkLabel} (${w.optional})`}
            value={state.link}
            onChange={(event) => set({ link: event.target.value })}
            placeholder={w.linkPlaceholder}
            inputMode="url"
            leading={<Icon icon={Link2} size={16} aria-hidden />}
            error={linkError !== null}
            errorMessage={
              linkError === 'not_https'
                ? w.linkNotHttps
                : linkError === 'too_long'
                  ? w.linkTooLong
                  : undefined
            }
            testId="social-link"
          />
        </Card>

        <section className="flex flex-col gap-12" aria-labelledby="social-preview-title">
          <h2
            id="social-preview-title"
            className="font-sans text-s14 font-w600 tracking-givmo text-text-strong"
          >
            {w.previewTitle}
          </h2>
          {views.length === 0 ? (
            <p
              className="font-sans text-s13 tracking-givmo text-text-secondary"
              {...testIdProp('social-preview-empty')}
            >
              {w.previewEmpty}
            </p>
          ) : (
            views.map((view) => (
              <VariantCard
                key={view.channel.connectionId}
                view={view}
                image={state.image}
                link={state.link.trim()}
                editing={editing.has(view.channel.connectionId)}
                onEdit={() =>
                  setEditing((current) => new Set(current).add(view.channel.connectionId))
                }
                onDone={() =>
                  setEditing((current) => {
                    const next = new Set(current);
                    next.delete(view.channel.connectionId);
                    return next;
                  })
                }
                onChange={(text) =>
                  set({ overrides: { ...state.overrides, [view.channel.connectionId]: text } })
                }
                onReset={() => {
                  const overrides = { ...state.overrides };
                  delete overrides[view.channel.connectionId];
                  set({ overrides });
                  setEditing((current) => {
                    const next = new Set(current);
                    next.delete(view.channel.connectionId);
                    return next;
                  });
                }}
              />
            ))
          )}
        </section>
      </div>

      <div
        className="flex flex-wrap items-center justify-end gap-8 border-t border-border-subtle pt-16"
        {...testIdProp('social-composer-actions')}
      >
        {failure ? (
          <ErrorMessage className="mr-auto" testId="social-composer-error">
            {actionErrorWords(failure)}
          </ErrorMessage>
        ) : reason !== null && (state.text.trim().length > 0 || state.channels.length > 0) ? (
          <span
            className="font-sans text-s13 font-w500 tracking-givmo text-warning-700"
            role="status"
            {...testIdProp('social-composer-reason')}
          >
            {reason}
          </span>
        ) : entry.suggestedAt ? (
          <span
            className="mr-auto font-sans text-s13 tracking-givmo text-text-secondary"
            {...testIdProp('social-suggested-time')}
          >
            {w.suggestedTime(whenWords(entry.suggestedAt))}
          </span>
        ) : null}
        {post !== null && post.deletable ? (
          <Button
            appearance="saas"
            tone="ghost"
            disabled={busy}
            onClick={() => setDeleting(true)}
            testId="social-delete-draft"
          >
            {w.deleteDraft}
          </Button>
        ) : null}
        <Button
          appearance="saas"
          tone="secondary"
          loading={saveDraft.isPending}
          disabled={busy || reason !== null}
          onClick={() => act(() => saveDraft.mutate())}
          testId="social-save-draft"
        >
          {w.saveDraft}
        </Button>
        <Button
          appearance="saas"
          tone="secondary"
          disabled={busy || reason !== null}
          onClick={() => act(() => setScheduling(true))}
          testId="social-schedule"
        >
          {w.schedule}
        </Button>
        <Button
          appearance="saas"
          tone="primary"
          loading={publish.isPending && !scheduling}
          disabled={busy || reason !== null}
          onClick={() => act(() => publish.mutate(null))}
          testId="social-publish-now"
        >
          {w.publishNow}
        </Button>
      </div>

      {scheduling ? (
        <ScheduleDialog
          suggestedAt={entry.suggestedAt}
          accounts={state.channels.length}
          busy={publish.isPending}
          error={publish.error ? actionErrorWords(publish.error) : null}
          onClose={() => {
            publish.reset();
            setScheduling(false);
          }}
          onConfirm={(when) => publish.mutate(when)}
        />
      ) : null}
      {unticking !== null ? (
        <Dialog.Root open onOpenChange={(next) => !next && setUnticking(null)}>
          <Dialog.Content
            aria-label={w.untickTitle(unticking.name, platformLabel(unticking.platform))}
            width={420}
            className="flex flex-col gap-16"
            testId="social-untick-dialog"
          >
            <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
              {w.untickTitle(unticking.name, platformLabel(unticking.platform))}
            </Dialog.Title>
            <p className="font-sans text-s14 tracking-givmo text-text-secondary">{w.untickBody}</p>
            <div className="flex justify-end gap-8">
              <Button appearance="saas" tone="secondary" onClick={() => setUnticking(null)}>
                {w.keepIt}
              </Button>
              <Button
                appearance="saas"
                tone="danger"
                onClick={() => {
                  flipChannel(unticking.connectionId);
                  setEditing((current) => {
                    const next = new Set(current);
                    next.delete(unticking.connectionId);
                    return next;
                  });
                  setUnticking(null);
                }}
                testId="social-untick-confirm"
              >
                {w.untickConfirm}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Root>
      ) : null}
      {deleting && orgRef !== null && postId !== null ? (
        <DeleteDraftDialog
          orgRef={orgRef}
          postId={postId}
          onClose={() => setDeleting(false)}
          onDeleted={() => void navigate({ to: SOCIAL_ROUTES.posts })}
        />
      ) : null}
    </SocialPageShell>
  );
}

function NoChannels({ manager }: { manager: boolean }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-wrap items-center gap-12" {...testIdProp('social-no-channels')}>
      <span className="font-sans text-s13 tracking-givmo text-text-secondary">
        {manager ? w.noChannels : w.askAdminToConnect}
      </span>
      {manager ? (
        <Button
          appearance="saas"
          tone="secondary"
          size="sm"
          onClick={() => void navigate({ to: SOCIAL_ROUTES.accounts })}
          testId="social-go-connect"
        >
          {w.connectAnAccount}
        </Button>
      ) : null}
    </div>
  );
}

function ChannelChip({
  channel,
  chosen,
  onToggle,
}: {
  channel: Channel;
  chosen: boolean;
  onToggle: () => void;
}) {
  // Chosen reads as chosen: filled, with a check. Not chosen reads as an option: quiet, its mark
  // faded, no heavy outline (the 2026-11-03 user test read the old outlined chip as the chosen one).
  return (
    <button
      type="button"
      aria-pressed={chosen}
      disabled={channel.needsReconnect}
      onClick={onToggle}
      title={channel.needsReconnect ? w.needsReconnect : undefined}
      className={cn(
        'inline-flex h-36 items-center gap-8 rounded-full border pr-12 pl-6 font-sans text-s13 font-w500 tracking-givmo transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
        chosen
          ? 'border-brand-600 bg-brand-600 text-white shadow-sm hover:bg-brand-700'
          : 'border-border-subtle bg-surface text-text-secondary hover:border-border-strong hover:text-text-strong',
        channel.needsReconnect && 'cursor-not-allowed opacity-60',
      )}
      {...testIdProp(`social-channel-${channel.connectionId}`)}
    >
      <span
        className={cn(
          'inline-flex size-[24px] items-center justify-center rounded-full bg-white',
          !chosen && 'opacity-60 grayscale',
        )}
      >
        <PlatformMark platform={channel.platform} size={18} />
      </span>
      <span className="max-w-[180px] truncate">{channel.name}</span>
      {chosen ? <Icon icon={Check} size={14} aria-hidden /> : null}
    </button>
  );
}

function CollateralStrip({
  items,
  busyId,
  disabled,
  onUse,
}: {
  items: readonly CollateralItem[];
  busyId: string | null;
  disabled: boolean;
  onUse: (item: CollateralItem) => void;
}) {
  return (
    <div className="flex flex-col gap-6" {...testIdProp('social-collateral')}>
      <span className="font-sans text-s12 tracking-givmo text-text-secondary">
        {w.fromCollateral}
      </span>
      <div className="flex flex-wrap gap-8">
        {items.slice(0, 8).map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            onClick={() => onUse(item)}
            aria-label={`${w.useGraphic}: ${item.title}`}
            className={cn(
              'relative size-[64px] overflow-hidden rounded-md border border-border-subtle',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-600',
              busyId === item.id && 'opacity-60',
            )}
            {...testIdProp(`social-collateral-${item.id}`)}
          >
            <img src={item.url} alt="" className="size-full object-cover" />
          </button>
        ))}
      </div>
    </div>
  );
}

function hintsFor(view: VariantView): string[] {
  return view.channel.platform === 'instagram' && view.link !== 'none' ? [w.hintInstagramLink] : [];
}

/** A link's host, as a link preview names it. */
function hostOf(link: string): string {
  try {
    return new URL(link).host.replace(/^www\./, '');
  } catch {
    return link;
  }
}

function problemWords(view: VariantView): string | null {
  const problem = view.problem;
  if (problem === null) return null;
  switch (problem.code) {
    case 'text_required':
      return w.problemTextRequired;
    case 'text_too_long':
      return w.problemTooLong(
        problem.over,
        platformLabel(view.channel.platform),
        problem.linkCounted,
      );
    case 'too_many_hashtags':
      return w.problemHashtags;
    case 'too_many_mentions':
      return w.problemMentions;
    case 'image_required':
      return w.problemImageRequired;
    case 'image_shape':
      return w.problemImageShape;
  }
}

/** One account's post as that platform will show it: its account, its words, its image and its
 *  link — a preview, not a form — with its count against the platform's own limit, and the way to
 *  give this account words of its own (and back). */
function VariantCard({
  view,
  image,
  link,
  editing,
  onEdit,
  onDone,
  onChange,
  onReset,
}: {
  view: VariantView;
  image: SocialImageOut | null;
  link: string;
  editing: boolean;
  onEdit: () => void;
  onDone: () => void;
  onChange: (text: string) => void;
  onReset: () => void;
}) {
  const id = `social-variant-card-${view.channel.connectionId}`;
  const problem = problemWords(view);
  // The base with no words yet says so in the main box, not on every card.
  const shown = problem !== null && (view.customized || view.problem?.code !== 'text_required');
  const over = view.sent > view.cap;
  const platform = platformLabel(view.channel.platform);
  const imageFirst = view.channel.platform === 'instagram';
  const previewImage =
    image && view.channel.platform !== 'test_social' ? (
      <img
        src={image.url}
        alt=""
        className="max-h-[168px] w-full rounded-md border border-border-subtle object-cover"
        {...testIdProp(`${id}-image`)}
      />
    ) : null;
  return (
    <Card className="flex flex-col gap-0 overflow-hidden p-0" testId={id}>
      <div className="flex items-center gap-10 px-16 pt-14 pb-10">
        <span className="inline-flex size-[32px] shrink-0 items-center justify-center rounded-full bg-surface-sunken">
          <PlatformMark platform={view.channel.platform} size={20} />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-sans text-s13 font-w600 tracking-givmo text-text-strong">
            {view.channel.name}
          </span>
          <span className="font-sans text-s12 tracking-givmo text-text-secondary">{platform}</span>
        </span>
        {view.customized ? (
          <span
            className="shrink-0 rounded-full bg-brand-50 px-8 py-2 font-sans text-s12 font-w500 tracking-givmo text-brand-700"
            {...testIdProp(`${id}-customized`)}
          >
            {w.customized}
          </span>
        ) : null}
      </div>
      <div className="flex flex-col gap-10 px-16 pb-12">
        {/* Instagram shows the image above its caption; the others show it below the words. */}
        {imageFirst ? previewImage : null}
        {editing ? (
          <>
            <Textarea
              aria-label={`${w.customize}: ${view.channel.name}`}
              value={view.text}
              onChange={(event) => onChange(event.target.value)}
              rows={5}
              testId={`${id}-text`}
            />
            <p
              className="font-sans text-s12 tracking-givmo text-text-secondary"
              {...testIdProp(`${id}-editing-hint`)}
            >
              {w.editingHint}
            </p>
          </>
        ) : (
          <p
            className="whitespace-pre-line break-words font-sans text-s14 tracking-givmo text-text-strong"
            {...testIdProp(`${id}-text`)}
          >
            {view.text || <span className="text-text-secondary">{w.textPlaceholder}</span>}
            {view.link === 'appended' && !editing ? (
              <>
                {'\n\n'}
                <span
                  className={cn(
                    'break-all',
                    // Instagram shows a caption's link as plain words; LinkedIn makes it a link.
                    view.channel.platform === 'instagram' ? 'text-text-strong' : 'text-brand-600',
                  )}
                  {...testIdProp(`${id}-link`)}
                >
                  {link}
                </span>
              </>
            ) : null}
          </p>
        )}
        {imageFirst ? null : previewImage}
        {view.link === 'apart' ? (
          <span
            className="flex flex-col gap-2 rounded-md border border-border-subtle bg-surface-sunken px-12 py-10"
            {...testIdProp(`${id}-link`)}
          >
            <span className="flex items-center gap-6 font-sans text-s12 font-w500 uppercase tracking-givmo text-text-secondary">
              <Icon icon={Link2} size={14} aria-hidden />
              {hostOf(link)}
            </span>
            <span className="break-all font-sans text-s13 tracking-givmo text-text-strong">
              {link}
            </span>
            <span className="font-sans text-s12 tracking-givmo text-text-secondary">
              {w.linkPreviewNote}
            </span>
          </span>
        ) : null}
        {hintsFor(view).map((hint) => (
          <p key={hint} className="font-sans text-s12 tracking-givmo text-text-secondary">
            {hint}
          </p>
        ))}
        {shown ? <ErrorMessage testId={`${id}-problem`}>{problem}</ErrorMessage> : null}
      </div>
      <div className="flex flex-wrap items-center gap-x-12 gap-y-4 border-t border-border-subtle px-16 py-10">
        <span
          className={cn(
            'font-sans text-s12 tabular-nums tracking-givmo',
            over ? 'text-danger-700' : 'text-text-secondary',
          )}
          {...testIdProp(`${id}-count`)}
        >
          {view.sent.toLocaleString()} / {view.cap.toLocaleString()}
          {view.link === 'appended' ? (
            <span {...testIdProp(`${id}-count-link`)}>{` · ${w.linkCounts}`}</span>
          ) : null}
        </span>
        <span className="ml-auto flex items-center gap-8">
          {view.customized ? (
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              onClick={onReset}
              testId={`${id}-reset`}
            >
              {w.useMainText}
            </Button>
          ) : null}
          {editing ? (
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={onDone}
              testId={`${id}-done`}
            >
              {w.doneEditing}
            </Button>
          ) : (
            <Button appearance="saas" tone="link" size="sm" onClick={onEdit} testId={`${id}-edit`}>
              {view.customized ? w.edit : w.customize}
            </Button>
          )}
        </span>
      </div>
    </Card>
  );
}

function ScheduleDialog({
  suggestedAt,
  accounts,
  busy,
  error,
  onClose,
  onConfirm,
}: {
  suggestedAt: string | null;
  accounts: number;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onConfirm: (when: Date) => void;
}) {
  const [start] = useState(() => startingTime(suggestedAt, new Date()));
  const [day, setDay] = useState(dateInput(start));
  const [time, setTime] = useState(timeInput(start));
  const [problem, setProblem] = useState<string | null>(null);
  const chosen = day && time ? new Date(`${day}T${time}`) : null;
  const readable = chosen !== null && !Number.isNaN(chosen.getTime());
  const confirm = () => {
    const when = new Date(`${day}T${time}`);
    const found = day && time ? scheduleProblem(when, new Date()) : 'invalid';
    if (found !== null) {
      setProblem(
        found === 'too_soon'
          ? w.scheduleTooSoon
          : found === 'too_far'
            ? w.scheduleTooFar
            : w.scheduleInvalid,
      );
      return;
    }
    setProblem(null);
    onConfirm(when);
  };
  return (
    <Dialog.Root open onOpenChange={(next) => !next && !busy && onClose()}>
      <Dialog.Content
        aria-label={w.scheduleTitle}
        width={440}
        className="flex flex-col gap-16"
        testId="social-schedule-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w600 tracking-givmo text-text-strong">
          {w.scheduleTitle}
        </Dialog.Title>
        <div className="grid grid-cols-2 gap-12">
          <Field
            label={w.scheduleDate}
            type="date"
            value={day}
            onChange={(event) => setDay(event.target.value)}
            testId="social-schedule-date"
          />
          <Field
            label={w.scheduleTime}
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
            testId="social-schedule-time"
          />
        </div>
        {readable ? (
          <p
            className="rounded-md bg-surface-sunken px-12 py-10 font-sans text-s14 font-w500 tracking-givmo text-text-strong"
            {...testIdProp('social-schedule-readback')}
          >
            {w.scheduleReadBack(whenWords(chosen.toISOString()), timeZoneName(), accounts)}
          </p>
        ) : null}
        <p
          className="font-sans text-s13 tracking-givmo text-text-secondary"
          {...testIdProp('social-schedule-zone')}
        >
          {w.scheduleWindow}
        </p>
        {problem || error ? (
          <ErrorMessage testId="social-schedule-error">{problem ?? error}</ErrorMessage>
        ) : null}
        <div className="flex justify-end gap-8">
          <Button appearance="saas" tone="secondary" disabled={busy} onClick={onClose}>
            {w.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            loading={busy}
            onClick={confirm}
            testId="social-schedule-confirm"
          >
            {w.scheduleConfirm}
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
